import { createApi } from '@reduxjs/toolkit/query/react'
import { PREDICTIONS_STATISTICS, PREDICTIONS_QUERY, PREDICTIONS_URL } from '../constants'
import baseQueryWithReauth from './baseQuery'
import {
  PredictionsStatisticsResp,
  QueryPredictionRequest,
  QueryPredictionsResp,
} from '../types/prediction'
import { apiTags } from './apiTags'

export const predictionApi = createApi({
  reducerPath: 'predictionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Prediction, apiTags.PredictionList, apiTags.PredictionStats, apiTags.Heatmap],

  endpoints: (builder) => ({
    getPredictionStatistics: builder.query<
      PredictionsStatisticsResp,
      {
        model_id: string
        uncertainty_threshold?: number
        tag_id?: string[]
      }
    >({
      query: (params) => ({
        url: PREDICTIONS_STATISTICS,
        params,
      }),
      providesTags: (result, error, { model_id }) => [
        { type: apiTags.PredictionStats, id: model_id },
        { type: apiTags.PredictionStats, id: 'LIST' },
      ],
    }),

    queryPredictions: builder.query<QueryPredictionsResp, QueryPredictionRequest>({
      query: (body) => ({
        url: PREDICTIONS_QUERY,
        method: 'POST',
        body,
      }),
      transformResponse: (response: QueryPredictionsResp) => {
        // Transform predictions to ensure both points and min/max are available for UI components
        // Note: Coordinate transformation from normalized (0-1) to pixel coordinates happens at component level
        // when content dimensions are available
        const transformedPredictions = response.predictions.map((prediction) => ({
          ...prediction,
          predictions: prediction.predictions.map((pred) => {
            if (!pred.bounding_boxes && !pred.oriented_bounding_boxes) {
              return pred
            }

            const bbox = pred.bounding_boxes || pred.oriented_bounding_boxes

            // Case 1: If points exist, ensure min/max is also available
            if (bbox?.points && bbox.points.length === 8) {
              // Extract x and y coordinates from points array
              const xValues = [bbox.points[0], bbox.points[2], bbox.points[4], bbox.points[6]]
              const yValues = [bbox.points[1], bbox.points[3], bbox.points[5], bbox.points[7]]

              // Note: Prediction API points are normalized (0-1),
              // min/max should be in the same coordinate system for consistency
              const xmin = Math.min(...xValues)
              const xmax = Math.max(...xValues)
              const ymin = Math.min(...yValues)
              const ymax = Math.max(...yValues)

              if (pred.bounding_boxes) {
                return {
                  ...pred,
                  bounding_boxes: {
                    ...bbox,
                    xmin,
                    xmax,
                    ymin,
                    ymax,
                  },
                }
              } else {
                return {
                  ...pred,
                  oriented_bounding_boxes: {
                    ...bbox,
                    xmin,
                    xmax,
                    ymin,
                    ymax,
                  },
                }
              }
            }

            // Case 2: If only min/max exists, create points array
            if (
              bbox?.xmin !== undefined &&
              bbox?.xmax !== undefined &&
              bbox?.ymin !== undefined &&
              bbox?.ymax !== undefined
            ) {
              // Create points array in clockwise order: top-left, top-right, bottom-right, bottom-left
              const points = [
                bbox.xmin,
                bbox.ymin, // top-left
                bbox.xmax,
                bbox.ymin, // top-right
                bbox.xmax,
                bbox.ymax, // bottom-right
                bbox.xmin,
                bbox.ymax, // bottom-left
              ]

              if (pred.bounding_boxes) {
                return {
                  ...pred,
                  bounding_boxes: {
                    ...bbox,
                    points,
                  },
                }
              } else {
                return {
                  ...pred,
                  oriented_bounding_boxes: {
                    ...bbox,
                    points,
                  },
                }
              }
            }

            return pred
          }),
        }))

        return {
          ...response,
          predictions: transformedPredictions,
        }
      },
      providesTags: (result) => [
        { type: apiTags.PredictionList, id: 'LIST' },
        ...(result?.predictions?.map(({ contentid }) => ({
          type: apiTags.Prediction,
          id: contentid,
        })) || []),
      ],
      keepUnusedDataFor: 300,
    }),

    getPredictionHeatmap: builder.query<string, { PredictionId: string }>({
      query: ({ PredictionId }) => ({
        url: `${PREDICTIONS_URL}/${PredictionId}/heatmap`,
        responseHandler: async (response) => {
          const blob = await response.blob()
          return URL.createObjectURL(blob)
        },
      }),
      merge: (currentCache, newItems) => currentCache || newItems,
      // keepUnusedDataFor: 300,
      providesTags: (result, error, { PredictionId }) => [
        { type: apiTags.Heatmap, id: PredictionId },
      ],
    }),
  }),
})

export const {
  useGetPredictionStatisticsQuery,
  useQueryPredictionsQuery,
  useGetPredictionHeatmapQuery,
} = predictionApi
